import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Shield, AlertTriangle, Package } from "lucide-react";
import { VulnerabilitySummary, Vulnerability } from "@/types/vulnerability";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CVSSDistributionCard } from "@/components/vulnerabilities/shared/CVSSDistributionCard";

interface VulnerabilitySummaryCardsProps {
  summary: VulnerabilitySummary;
  vulnerabilities: Vulnerability[];
}

export function VulnerabilitySummaryCards({ summary, vulnerabilities }: VulnerabilitySummaryCardsProps) {
  const severityData = [
    { name: 'Critical', value: summary.critical, color: 'hsl(0 84.2% 60.2%)' },
    { name: 'High', value: summary.high, color: 'hsl(30 80% 55%)' },
    { name: 'Medium', value: summary.medium, color: 'hsl(45 90% 55%)' },
    { name: 'Low', value: summary.low, color: 'hsl(220 70% 50%)' },
  ].filter(item => item.value > 0);

  return (
    <div className="space-y-4 mb-6">
      {/* CVSS Distribution - Full Width */}
      <CVSSDistributionCard vulnerabilities={vulnerabilities} />
      
      {/* Other Cards - 3 Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Vulnerabilities by Severity */}
      <GlassCard variant="default" className="group">
        <GlassCardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <GlassCardTitle className="text-sm font-medium">Vulnerabilities by Severity</GlassCardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 backdrop-blur-sm border border-primary/20 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
            <Shield className="h-4 w-4 text-primary" />
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    padding: '8px 12px',
                  }}
                  labelStyle={{ 
                    color: 'hsl(var(--popover-foreground))',
                    fontWeight: 600,
                  }}
                  formatter={(value: number) => [value, 'Count']}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => value}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-center mt-2">
            <p className="text-2xl font-bold">{summary.total}</p>
            <p className="text-xs text-muted-foreground">Total Vulnerabilities</p>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Exploitability Percentage */}
      <GlassCard variant="default" className="group">
        <GlassCardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <GlassCardTitle className="text-sm font-medium">Actively Exploited</GlassCardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-500/5 backdrop-blur-sm border border-orange-500/20 group-hover:from-orange-500/20 group-hover:to-orange-500/10 transition-all duration-300">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="flex flex-col items-center justify-center h-[200px]">
            <div className="relative">
              <svg className="w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="hsl(var(--muted))"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="hsl(30 80% 55%)"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${summary.actively_exploited_percentage * 3.51} 351.86`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold">
                  {summary.actively_exploited_percentage.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
          <div className="text-center mt-2">
            <p className="text-xl font-semibold">{summary.actively_exploited_count}</p>
            <p className="text-xs text-muted-foreground">Known Exploits in the Wild</p>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Top Software with Vulnerabilities */}
      <GlassCard variant="default" className="group">
        <GlassCardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <GlassCardTitle className="text-sm font-medium">Top Vulnerable Software</GlassCardTitle>
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 backdrop-blur-sm border border-blue-500/20 group-hover:from-blue-500/20 group-hover:to-blue-500/10 transition-all duration-300">
            <Package className="h-4 w-4 text-blue-500" />
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <TooltipProvider>
            <div className="space-y-3 mt-4">
              {summary.top_software.slice(0, 5).map((software, index) => (
                <div key={index} className="flex items-center justify-between gap-2 overflow-hidden">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm font-medium truncate cursor-default">{software.name}</p>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{software.name}</p>
                        </TooltipContent>
                      </Tooltip>
                      <p className="text-xs text-muted-foreground">{software.os_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                    <span className="text-sm font-semibold">{software.count}</span>
                    <span className="text-xs text-muted-foreground">CVEs</span>
                  </div>
                </div>
              ))}
            </div>
          </TooltipProvider>
        </GlassCardContent>
      </GlassCard>
      </div>
    </div>
  );
}
